"use client"

import { Button } from "@medusajs/ui"
import { isEqual } from "lodash"
import { useParams } from "next/navigation"
import { useMemo, useRef, useState } from "react"
import { Minus, Plus } from "lucide-react"

import { useIntersection } from "@lib/hooks/use-in-view"
import Divider from "@modules/common/components/divider"
import OptionSelect from "@modules/products/components/product-actions/option-select"

import MobileActions from "./mobile-actions"
import ProductPrice from "../product-price"
import { addToCart } from "@lib/data/cart"
import { emitCartUpdate, emitCartDelta } from "@lib/cart-event"
import { HttpTypes } from "@medusajs/types"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
}

const optionsAsKeymap = (variantOptions: any) => {
  return variantOptions?.reduce((acc: Record<string, string | undefined>, varopt: any) => {
    if (varopt.option && varopt.value !== null && varopt.value !== undefined) {
      acc[varopt.option.title] = varopt.value
    }
    return acc
  }, {})
}

export default function ProductActions({
  product,
  region,
  disabled,
}: ProductActionsProps) {
  // Preselect options for single-variant products at init time (not in useEffect)
  // so SSR renders the correct in-stock status for crawlers/SEO
  const [options, setOptions] = useState<Record<string, string | undefined>>(() => {
    if (product.variants?.length === 1) {
      return optionsAsKeymap(product.variants[0].options) ?? {}
    }
    return {}
  })
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [cartError, setCartError] = useState<string | null>(null)
  const countryCode = useParams().countryCode as string

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return
    }

    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  // update the options when a variant is selected
  const setOptionValue = (title: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [title]: value,
    }))
  }

  // check if the selected variant is in stock
  const inStock = useMemo(() => {
    // If we don't manage inventory, we can always add to cart
    if (selectedVariant && !selectedVariant.manage_inventory) {
      return true
    }

    // If we allow back orders on the variant, we can add to cart
    if (selectedVariant?.allow_backorder) {
      return true
    }

    // If there is inventory available, we can add to cart
    if (
      selectedVariant?.manage_inventory &&
      (selectedVariant?.inventory_quantity || 0) > 0
    ) {
      return true
    }

    // Otherwise, we can't add to cart
    return false
  }, [selectedVariant])

  const actionsRef = useRef<HTMLDivElement>(null)

  const inView = useIntersection(actionsRef, "0px")

  // add the selected variant to the cart
  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return null

    setIsAdding(true)
    setCartError(null)

    // Optimistically update badge immediately — don't wait for server
    emitCartDelta(quantity)

    try {
      await addToCart({
        variantId: selectedVariant.id,
        quantity: quantity,
        countryCode,
      })
      emitCartUpdate()
    } catch (err: any) {
      // Revert optimistic delta on failure
      emitCartDelta(-quantity)
      console.error('[handleAddToCart]', err)
      setCartError(err?.message || 'Failed to add to cart. Please try again.')
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <>
      <div className="flex flex-col gap-y-6" ref={actionsRef}>
        <div>
          {(product.variants?.length ?? 0) > 1 && (
            <div className="flex flex-col gap-y-4">
              {(product.options || []).map((option) => {
                return (
                  <div key={option.id}>
                    <OptionSelect
                      option={option}
                      current={options[option.title ?? ""]}
                      updateOption={setOptionValue}
                      title={option.title ?? ""}
                      data-testid="product-options"
                      disabled={!!disabled || isAdding}
                    />
                  </div>
                )
              })}
              <Divider />
            </div>
          )}
        </div>

        <ProductPrice product={product} variant={selectedVariant} />

        {/* Stock Status Badge */}
        <div className="mb-2">
          {!selectedVariant ? (
            <span className="inline-block bg-gray-50 text-gray-600 text-sm font-medium px-4 py-2 rounded-full">
              Select a variant
            </span>
          ) : inStock ? (
            <span className="inline-block bg-green-50 text-green-700 text-sm font-medium px-4 py-2 rounded-full">
              In Stock - Ready to Ship
            </span>
          ) : (
            <span className="inline-block bg-red-50 text-red-700 text-sm font-medium px-4 py-2 rounded-full">
              Sold Out
            </span>
          )}
        </div>

        {/* Quantity Selector */}
        <div className="border-t border-gray-200 pt-6">
          <label className="block text-sm font-bold tracking-[0.15em] uppercase mb-4 text-ui-fg-base">
            Quantity
          </label>
          <div className="flex items-center border-2 border-gray-300 w-fit">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="p-4 hover:bg-gray-50 transition-colors border-r border-gray-300"
              aria-label="Decrease quantity"
              disabled={!!disabled || isAdding}
            >
              <Minus size={16} strokeWidth={2.5} />
            </button>
            <span className="px-8 text-base font-semibold min-w-[60px] text-center">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="p-4 hover:bg-gray-50 transition-colors border-l border-gray-300"
              aria-label="Increase quantity"
              disabled={!!disabled || isAdding}
            >
              <Plus size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {cartError && (
          <p className="text-red-600 text-sm px-1">{cartError}</p>
        )}
        <Button
          onClick={handleAddToCart}
          disabled={!inStock || !selectedVariant || !!disabled || isAdding}
          variant="primary"
          className="w-full h-12"
          isLoading={isAdding}
          data-testid="add-product-button"
        >
          {!selectedVariant
            ? "Select variant"
            : !inStock
            ? "Out of stock"
            : "Add to cart"}
        </Button>
        <MobileActions
          product={product}
          variant={selectedVariant}
          options={options}
          updateOptions={setOptionValue}
          inStock={inStock}
          handleAddToCart={handleAddToCart}
          isAdding={isAdding}
          show={!inView}
          optionsDisabled={!!disabled || isAdding}
        />
      </div>
    </>
  )
}
